// Phase 4 Task 9 — Drag-and-drop section builder.
//
// Behaviors:
//   - SortableJS reorders <li data-section-target> elements; onEnd persists.
//   - Per-section enabled checkbox toggle persists.
//   - Keyboard reorder via ArrowUp/ArrowDown on the drag handle (WCAG 2.1.1
//     [CRITICAL FIX #10] UI Designer + UX Researcher review).
//   - Persist via PATCH JSON; server returns Turbo Stream → renderStreamMessage.
//
// [CRITICAL FIX #6] Turbo MUST be imported explicitly (not on window).
// [CRITICAL FIX #7] Persist does NOT replace the builder frame mid-drag —
//   the controller owns its DOM during a drag operation. Server response
//   only flashes a toast in #report_template_flash.
import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import * as Turbo from "@hotwired/turbo"

export default class extends Controller {
  static targets = ["list", "section"]
  static values  = {
    updateUrl: String,
    csrfToken: String
  }

  connect() {
    this.sortable = new Sortable(this.listTarget, {
      animation: 150,
      handle: ".drag-handle",
      ghostClass: "section-ghost",
      onEnd: () => this.persist()
    })
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  // Toggle handler — fires when a section's enabled checkbox changes.
  toggle() {
    this.persist()
  }

  // Arrow-key reorder — keyboard accessibility fallback.
  moveUp(event) {
    const li = event.currentTarget.closest("[data-report-template-builder-target='section']")
    const prev = li?.previousElementSibling
    if (prev) {
      li.parentNode.insertBefore(li, prev)
      this.persist()
      event.currentTarget.focus()
    }
    event.preventDefault()
  }

  moveDown(event) {
    const li = event.currentTarget.closest("[data-report-template-builder-target='section']")
    const next = li?.nextElementSibling
    if (next) {
      li.parentNode.insertBefore(next, li)
      this.persist()
      event.currentTarget.focus()
    }
    event.preventDefault()
  }

  async persist() {
    const config = this.serializeConfig()
    try {
      const response = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ report_template: { config: config } })
      })
      if (response.ok) {
        const turboHtml = await response.text()
        if (turboHtml && turboHtml.trim().length > 0) {
          Turbo.renderStreamMessage(turboHtml)
        }
      } else {
        console.warn(`[report-template-builder] persist returned ${response.status}`)
      }
    } catch (err) {
      console.error("[report-template-builder] persist failed:", err)
    }
  }

  serializeConfig() {
    const sections = this.sectionTargets.map((el, idx) => ({
      key:      el.dataset.sectionKey,
      enabled:  el.querySelector("input[type='checkbox']").checked,
      position: idx
    }))
    // Existing config fields not modified by the UI are preserved
    // by the server (params.permit(:name, :notes_block, config: {})
    // accepts whatever shape we send). Send the canonical schema.
    return {
      version: 1,
      sections: sections,
      date_range: { kind: "event", start: null, end: null },
      event_exhibitor_ids: [],
      include_logo: true,
      include_notes: true
    }
  }
}
